import { commandsContext } from '@insify/funnel-components';
import { ensureDeviceId } from '@insify/ui-utils';
import React from 'react';
import { useQueryClient } from 'react-query';

import { isLocalDev } from '../../util/env';
import { PARAMS_KEY } from '../models/queries';

import type { Commands } from '../models/commands';
import type { Params } from '../models/queries';
import type { UseCommandsResult } from '@insify/funnel-components';

const addDeviceIdToCommand = (command: Commands) => {
  const deviceId = ensureDeviceId(isLocalDev ? undefined : '.insify.nl');
  return {
    ...command,
    meta: {
      ...command.meta,
      deviceId,
    },
  };
};

export function useCommands() {
  const queryClient = useQueryClient();
  const context = React.useContext(commandsContext) as UseCommandsResult<Commands>;
  if (context === undefined) {
    throw new Error('useCommands must be used within a PncCommandsAndQueryProvider');
  }

  type SendCommandWithOptimisticUpdate = (commandWithParams: {
    command: Parameters<typeof context.sendCommand>[0];
    params: Partial<Params>;
  }) => Promise<void>;

  const sendCommandWithOptimisticUpdate: SendCommandWithOptimisticUpdate = ({ params, command }) => {
    queryClient.setQueryData<Partial<Params>>(PARAMS_KEY, (previousValues) => ({
      ...previousValues,
      ...params,
    }));
    return context.sendCommand(addDeviceIdToCommand(command));
  };

  const sendCommand = (command: Commands) => {
    return context.sendCommand(addDeviceIdToCommand(command));
  };

  const sendCommandAndWaitForInvalidations = (command: Commands) => {
    return context.sendCommandAndWaitForInvalidations(addDeviceIdToCommand(command));
  };

  const sendMultilpleCommandsWithOptimisticUpdate = async ({
    params,
    commands,
  }: {
    params: Partial<Params>;
    commands: Parameters<typeof context.sendMultipleCommands>[0];
  }) => {
    queryClient.setQueryData<Partial<Params>>(PARAMS_KEY, (previousValues) => ({
      ...previousValues,
      ...params,
    }));
    const commandsWithDeviceId = commands.map(addDeviceIdToCommand);
    return context.sendMultipleCommands(commandsWithDeviceId);
  };

  return {
    ...context,
    sendCommandWithOptimisticUpdate,
    sendMultilpleCommandsWithOptimisticUpdate,
    sendCommand,
    sendCommandAndWaitForInvalidations,
  };
}
